import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { RootStoreContext } from '../core/root-store';
import { doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import PersonalityResultsUi from './ui/personality-results.ui';
import { PersonalityArchetype, PersonalityImage } from './ui/personality-results.ui';

const PersonalityResultsContainer = observer(() => {
  const rootStore = useContext(RootStoreContext);
  const userStore = rootStore.userStore;
  const navigate = useNavigate();

  const [archetype, setArchetype] = useState<PersonalityArchetype | null>(null);
  const [image, setImage] = useState<PersonalityImage | null>(null);
  const [loading, setLoading] = useState(true);

  const personalityCode = userStore.user?.personality_results?.personality_code;

  useEffect(() => {
    const fetchPersonalityData = async () => {
      if (!personalityCode) {
        navigate('/dashboard');
        return;
      }

      // Add minimum loading time for better UX (2-3 seconds)
      const minLoadingTime = new Promise(resolve => setTimeout(resolve, 2500));

      try {
        setLoading(true);

        // Fetch archetype data
        const archetypeDoc = await getDoc(doc(db, 'forms/personalityArchetypes'));
        const archetypeData = archetypeDoc.data();

        if (archetypeData?.types?.[personalityCode]) {
          setArchetype(archetypeData.types[personalityCode]);
        } else {
          await minLoadingTime;
          navigate('/dashboard');
          return;
        }

        // Fetch single PNG image for this personality code from Storage
        try {
          const personalityImageRef = ref(storage, `personalityArchetypes/${personalityCode}/${personalityCode}.png`);
          const imageUrl = await getDownloadURL(personalityImageRef);
          setImage({
            url: imageUrl,
            name: personalityCode,
          });
        } catch {
          setImage(null);
        }

        // Wait for both data fetching and minimum loading time
        await minLoadingTime;
      } catch (err) {
        await minLoadingTime;
        navigate('/dashboard');
        return;
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalityData();
  }, [personalityCode, navigate]);

  const handleContinueToDashboard = () => {
    navigate('/dashboard');
  };

  // Don't immediately redirect if no personality code - wait for data to load
  // The useEffect will handle redirects if needed after trying to fetch data

  return (
    <PersonalityResultsUi
      personalityCode={personalityCode}
      archetype={archetype}
      image={image}
      loading={loading}
      onContinue={handleContinueToDashboard}
    />
  );
});

export default PersonalityResultsContainer;
